import React, { ChangeEvent } from 'react';
import cx from 'classnames';
import { connect } from 'react-redux';
import { RootState } from '../../../slices/rootReducer';
import Icon from '@ant-design/icons';
import { Form, Input } from 'antd';
import { ReactComponent as SxPlusCircle } from '../../../assets/sightx-icons/svg/sx-plus-circle.svg';
import { FormInstance } from 'antd/lib/form';
import { ReactComponent as SxClose } from '../../../assets/sightx-icons/svg/sx-close.svg';
import { ReactComponent as SxCloseError } from '../../../assets/sightx-icons/svg/x-close-error.svg';
import { validateMessages } from '../modal/constants';
import { isValidString as isNotEmptyString } from '../../common/utils/string';
import {
  actions,
  EmailStatusType,
  selectors as userBulkCreationSelectors,
  UserErrorCode
} from '../../../slices/users/bulkCreation';
import { LinkButton } from '../../common/linkButton';
import {
  checkEmail,
  ICheckEmailPayload,
  IUserForBulkCreation
} from '../../../slices/users/bulkCreation/index.thunks';
import { Link } from 'react-router-dom';
import { AccessTypes } from '../../common/domain/accountAccessType';
import { MAX_ALLOWED_SEATS_FOR_PRO } from '../../settings/subscription/common/constants';
import { fetchAccountById } from '../../../slices/accounts/accounts.thunks';
import { selectors as accountListSelectors } from '../../../slices/accounts/list';

import styles from './addUsersInBulkForm.module.scss';

interface IState {
  usersToAddList: IUserForBulkCreation[];
  isAddToListEnabled: boolean;
}

interface IStateProps {
  checkEmailLoading: boolean;
  emailStatus: EmailStatusType | null;
  loading: boolean;
  errors: string[];
  errorMessage: string;
  emailsWithErrors: Set<string>;
  accountAccessType: AccessTypes | undefined;
  availableSeats: number;
  purchasedSeats: number;
}

interface IDispatchProps {
  checkEmail: (arg: ICheckEmailPayload) => ReturnType<ReturnType<typeof checkEmail>>;
  fetchAccountById: (arg: string) => ReturnType<ReturnType<typeof fetchAccountById>>;
  clearValues: typeof actions.clearValues;
  clearCheckEmailValues: typeof actions.clearCheckEmailValues;
  setEmailIsAlreadyAddedToList: typeof actions.setEmailIsAlreadyAddedToList;
}

interface IProps {
  accountId: string;
  isForSxAdmin: boolean;
  isPurchasingSeats?: boolean;
  onUserListChange: (usersToAddList: IUserForBulkCreation[]) => void;
}

type AddUsersInBulkModalProps = IStateProps & IDispatchProps & IProps;
class AddUsersInBulkFormBase extends React.Component<AddUsersInBulkModalProps, IState> {
  private readonly formRef: React.RefObject<FormInstance>;

  constructor(props: AddUsersInBulkModalProps) {
    super(props);
    this.state = {
      usersToAddList: [],
      isAddToListEnabled: false
    };
    this.handleOnEmailChange = this.handleOnEmailChange.bind(this);
    this.handleAddToList = this.handleAddToList.bind(this);
    this.updateIsAddToListEnabled = this.updateIsAddToListEnabled.bind(this);
    this.addUserAndResetForm = this.addUserAndResetForm.bind(this);
    this.formRef = React.createRef<FormInstance>();
  }

  componentDidUpdate(prevProps: AddUsersInBulkModalProps, prevState: IState) {
    const { errors, emailsWithErrors } = this.props;
    if (errors && errors.length > 0 && this.state.usersToAddList.every((user) => !emailsWithErrors.has(user.email))) {
      this.props.clearValues();
    }
    if (prevProps.loading && !this.props.loading && this.props.errors.length === 0 &&
      this.props.emailsWithErrors.size === 0 && !this.props.errorMessage) {
      this.props.fetchAccountById(this.props.accountId);
    }
  }

  componentWillUnmount() {
    this.props.clearValues();
  }

  private updateIsAddToListEnabled() {
    const areSeatsAvailable = (this.props.availableSeats - this.state.usersToAddList.length) > 0;
    const formValues = this.formRef.current?.getFieldsValue();
    const areFieldsFilled = !!formValues.firstName && !!formValues.lastName && !!formValues.email;
    this.setState({ isAddToListEnabled: areSeatsAvailable && areFieldsFilled });
  }

  private async handleAddToList(user: IUserForBulkCreation) {
    const userToAdd: IUserForBulkCreation = { ...user };
    const isAlreadyAdded = this.state.usersToAddList.some(u => u.email === user.email);
    if (isAlreadyAdded) {
      this.props.setEmailIsAlreadyAddedToList();
      return;
    }
    const isValidFirstName = isNotEmptyString(user.firstName);
    if (!isValidFirstName) {
      this.resetFormField('firstName');
    }
    const isValidLastName = isNotEmptyString(user.lastName);
    if (!isValidLastName) {
      this.resetFormField('lastName');
    }
    userToAdd.firstName = user.firstName.trim();
    userToAdd.lastName = user.lastName.trim();
    this.formRef.current?.validateFields();
    if (isValidFirstName && isValidLastName) {
      await this.props.checkEmail({ email: userToAdd.email });
      const { emailStatus } = this.props;
      if (emailStatus === EmailStatusType.Valid) {
        this.addUserAndResetForm(userToAdd);
      }
    }
  }

  private addUserAndResetForm(userToAdd: IUserForBulkCreation) {
    this.setState((prevState) => {
      return {
        usersToAddList: [...prevState.usersToAddList, userToAdd],
        formValues: { firstName: '', lastName: '', email: '', isOwner: false },
        errorMessage: '',
        isAddToListEnabled: false
      };
    }, () => {
      if (this.formRef.current) {
        this.formRef.current.resetFields();
        this.updateIsAddToListEnabled();
      }
      this.props.onUserListChange(this.state.usersToAddList);
    });
  }

  private resetFormField = (fieldName: string) => {
    if (this.formRef.current) {
      this.formRef.current.setFieldsValue({
        [fieldName]: ''
      });
    }
  };

  private handleOnEmailChange(event: ChangeEvent<HTMLInputElement>) {
    const value = event.currentTarget.value.trim();
    this.formRef.current?.setFieldsValue({ email: value });
    this.updateIsAddToListEnabled();
    const { emailStatus } = this.props;
    if (emailStatus) {
      this.props.clearCheckEmailValues();
    }
  }

  private removeUserFromListByEmail(email: string) {
    this.setState((state) => {
      const usersToAddList = state.usersToAddList.filter((user) => user.email !== email);
      return {
        usersToAddList
      };
    }, () => {
      this.props.onUserListChange(this.state.usersToAddList);
    });
  }

  private renderEmailField() {
    return (
      <Form.Item
        name="email"
        label="Email address"
        className={styles.formItem}
        rules={[
          { type: 'email', message: 'Please enter a valid email address' },
          { required: true, message: 'Email address is required' }
        ]}
      >
        <Input placeholder="Email address" onChange={this.handleOnEmailChange} />
      </Form.Item>
    );
  }

  private renderForm() {
    const { availableSeats, checkEmailLoading, accountAccessType } = this.props;
    const { usersToAddList, isAddToListEnabled } = this.state;
    const areSeatsAvailable = availableSeats - usersToAddList.length > 0;
    if (!areSeatsAvailable) {
      if (AccessTypes.Enterprise === accountAccessType) {
        return (
          <div className={cx(styles.noMoreSeatsMessage)}>
            <div>
              You have no more unassigned seats.
            </div>
            <div>
              To add additional users, purchase more seats
              <br />on the <Link className={styles.subscription} to="/settings/subscription">Subscription page</Link>.
            </div>
          </div>
        );
      }
      return (
        <div className={styles.noMoreSeatsMessage}>
          There are no more unassigned seats. To send invitations to more users, add more seats on the previous page.
        </div>
      );
    }
    return (
      <Form
        ref={this.formRef}
        validateMessages={validateMessages}
        onFinish={this.handleAddToList}
        requiredMark={false}
        colon={false}
        layout="vertical"
        className={cx(styles.form, styles.trialsOfTiers)}
        onValuesChange={this.updateIsAddToListEnabled}
      >
        <div className={styles.fields}>
          <div className={cx(styles.formRow, styles.names)}>
            <Form.Item
              name="firstName"
              rules={[{ required: true }]}
              label="First name"
              className={styles.formItem}
            >
              <Input placeholder="First name" />
            </Form.Item>
            <Form.Item
              name="lastName"
              rules={[{ required: true }]}
              label="Last name"
              className={styles.formItem}
            >
              <Input placeholder="Last name" />
            </Form.Item>
          </div>
          {this.renderEmailField()}
          {this.renderAddToListError()}
          {areSeatsAvailable && <span className={cx(styles.formItem, styles.addToListButton)}>
            <LinkButton
              htmlType="submit"
              loading={checkEmailLoading}
              icon={<Icon component={SxPlusCircle} />}
              disabled={!isAddToListEnabled}
            >
              Add to list
            </LinkButton>
          </span>}
        </div>
      </Form>
    );
  }

  private renderDescription() {
    const { availableSeats, isForSxAdmin, purchasedSeats = 0, isPurchasingSeats, accountAccessType } = this.props;
    const canAddMultipleSeats = availableSeats > 1;
    const seatsLabel = canAddMultipleSeats ? 'seats' : 'seat';
    const areSeatsAvailable = availableSeats - this.state.usersToAddList.length > 0;
    if (isForSxAdmin) {
      if (!areSeatsAvailable) {
        return;
      }
      return (
        <div>There {canAddMultipleSeats ? 'are' : 'is'} <strong>{availableSeats}</strong> open {seatsLabel} to assign to new users. Add more seats on the
          previous page.
        </div>
      );
    }
    const canPurchaseSeats = AccessTypes.Enterprise === accountAccessType || purchasedSeats < MAX_ALLOWED_SEATS_FOR_PRO;
    if (canPurchaseSeats && !isPurchasingSeats) {
      return (
        <div>
          You have <strong>{availableSeats}</strong> open {seatsLabel} to assign to new users.
        </div>
      );
    }
    return (
      <div>
        You have <strong>{availableSeats}</strong> open {seatsLabel} to assign to new users.
      </div>
    );
  }

  private renderAddToListError() {
    const { emailStatus } = this.props;
    const email = this.formRef.current?.getFieldValue('email');
    if (!emailStatus || !email) {
      return null;
    }
    return (
      <p className={styles.addToListError}>{this.getCheckEmailErrorMessage(emailStatus)}</p>
    );
  }

  private getCheckEmailErrorMessage(emailStatus: EmailStatusType) {
    switch (emailStatus) {
    case EmailStatusType.Invalid:
      return 'The email is invalid';
    case EmailStatusType.AlreadyExists:
      return 'As SightX user already exists with this email address';
    case EmailStatusType.AlreadyAddedToList:
      return 'The email is already added to the list below';
    default:
      return null;
    }
  }

  private renderUsersToAddList() {
    const { emailsWithErrors } = this.props;
    const { usersToAddList } = this.state;

    return (
      <div className={styles.usersToAdd}>
        {usersToAddList?.map((user) => {
          const userEmail = user.email;
          const isUserWithError = emailsWithErrors.has(userEmail);
          return (
            <div className={cx(styles.userToAdd, { [styles.userToAddWithError]: isUserWithError })} key={userEmail}>
              <span>{userEmail}</span>
              <Icon
                className={styles.removeEmailIcon}
                component={isUserWithError ? SxCloseError : SxClose}
                onClick={this.removeUserFromListByEmail.bind(this, userEmail)}
              />
            </div>
          );
        })}
      </div>
    );
  }

  render() {
    return (
      <>
        {this.renderDescription()}
        {this.renderForm()}
        {this.renderUsersToAddList()}
      </>
    );
  }
}

const getErrorMessage = (errorCode: UserErrorCode, otherMessage: string): string => {
  switch (errorCode) {
  case UserErrorCode.EmailAlreadyExists:
    return 'Some emails already have a SightX account';
  case UserErrorCode.InvalidEmail:
    return 'Some emails have an invalid format';
  case UserErrorCode.InvalidFirstName:
    return 'Some users have an invalid first name';
  case UserErrorCode.InvalidLastName:
    return 'Some users have an invalid last name';
  default:
    return otherMessage;
  }
};

const mapStateToProps = (state: RootState, props: IProps): IStateProps => {
  const userBulkCreationErrors = userBulkCreationSelectors.getErrors(state);
  const userBulkCreationErrorMessage = userBulkCreationSelectors.getMessage(state);
  const errors = userBulkCreationErrors.map(errorCode => getErrorMessage(errorCode, userBulkCreationErrorMessage));
  const account = accountListSelectors.getAccountById(state, props.accountId);
  const accountAccessType = account?.accessType;
  const availableSeats = (account?.seatsPurchased || 0) - (account?.seatsUsed || 0);
  return {
    checkEmailLoading: userBulkCreationSelectors.getCheckEmailLoading(state),
    emailStatus: userBulkCreationSelectors.getEmailStatus(state),
    loading: userBulkCreationSelectors.getLoading(state),
    errors,
    errorMessage: userBulkCreationErrorMessage,
    emailsWithErrors: new Set<string>(userBulkCreationSelectors.getEmailsWithErrors(state) ?? []),
    purchasedSeats: account?.seatsPurchased || 0,
    accountAccessType,
    availableSeats
  };
};

export const AddUsersInBulkForm = connect(mapStateToProps, {
  checkEmail,
  clearValues: actions.clearValues,
  clearCheckEmailValues: actions.clearCheckEmailValues,
  setEmailIsAlreadyAddedToList: actions.setEmailIsAlreadyAddedToList,
  fetchAccountById
})(AddUsersInBulkFormBase);
